import {states} from '../commons/constants/portaro.constants';
import {removeByProperty} from '../shared/utils/array-utils.fp';
import {getErrorMessage} from '../shared/utils/data-service-utils';
import type {IQService} from 'angular';

export interface Page<T = any> {
    pageNumber: number;
    first: boolean;
    last: boolean;
    items: T[];
}

export default class Pagination {

    public pages: Page[] = [];
    public showedPageNumbers = [];
    public items = [];
    public state = {
        page: states.NORMAL,
        previousPage: states.NORMAL,
        nextPage: states.NORMAL,
        distantPage: states.NORMAL
    };

    private pageSize;
    private totalPages = -1;
    private previousPageNumber = -1;
    private nextPageNumber = -1;
    private error;

    public static createWithNotPagedFunction($q: IQService,
                                             loadItemsPromiseFunction: (pageNumber: number, allParams: any) => Promise<any[]>,
                                             pageSize?: number): Pagination {
        const loadPagePromiseFunction = async (pageNumber, allParams) => {
            const items: any[] = await loadItemsPromiseFunction(pageNumber, allParams);
            return {
                items,
                pageNumber,
                first: pageNumber === 1,
                last: items.length === 0 || (pageSize && items.length < pageSize)
            };
        };
        return new Pagination($q, loadPagePromiseFunction);
    }

    constructor(private readonly $q: IQService,
                private readonly loadPagePromiseFunction: (pageNumber: number, allParams: any) => Promise<Page<any>>,
                private readonly requestPageFunction?: (pageNumber: number) => Promise<void>) {
        this.requestPageFunction = requestPageFunction || ((pageNumber: number) => this.loadPage(pageNumber, null))
    }

    public async loadPage(pageNumber, forwardedParams): Promise<void> {
        this.error = null;
        const resetState = this.makeProcessingState(pageNumber);

        if (!this.isContiguous(pageNumber)) { // pokud neni tato stranka primo navazujici (netvori souvislou radu se soucasnymi), ostatni smazeme
            this.clear();
        }

        try {
            const page: Page<any> = await this.loadPagePromiseFunction(pageNumber, forwardedParams);
            this.addPage(page);
        } catch (err) {
            this.error = getErrorMessage(err);
            throw err;
        } finally {
            resetState();
        }
    }

    public requestPage(pageNumber: number) {
        return this.requestPageFunction(pageNumber);
    }

    public requestNextPage() {
        if (this.hasNext()) {
            return this.requestPage(this.nextPageNumber);
        }
        return this.$q.reject(new Error('Not any next page'));
    }

    public requestPreviousPage() {
        if (this.hasPrevious()) {
            return this.requestPage(this.previousPageNumber);
        }
        return this.$q.reject(new Error('Not any previous page'));
    }

    public setTotalElements(totalElements) {
        if (this.pageSize) {
            this.totalPages = Math.ceil(totalElements / this.pageSize);
        }
    }

    public setPageSize(ps) {
        this.pageSize = ps;
    }

    public getPreviousPageNumber() {
        return this.previousPageNumber;
    }

    public getNextPageNumber() {
        return this.nextPageNumber;
    }

    public getLowestPage() {
        return this.pages.length === 0 ? null : this.pages[0];
    }

    public getHighestPage() {
        return this.pages.length === 0 ? null : this.pages[this.pages.length - 1];
    }

    public hasPrevious() {
        return this.previousPageNumber !== -1;
    }

    public hasNext() {
        return this.nextPageNumber !== -1;
    }

    public getPageSize() {
        return this.pageSize;
    }

    public getTotalPages() {
        return this.totalPages;
    }

    public clear() {
        this.pages.length = 0;
        this.items.length = 0;
        this.previousPageNumber = -1;
        this.nextPageNumber = -1;
    }

    public getError() {
        return this.error;
    }

    public reset() {
        this.clear();
        this.totalPages = -1;
        this.recomputePageNumbers();
    }

    /**
     * Funkce, ktera nastavi stavu nektere stranky (podle pageNumber) PROCESSING a vraci funkci pro vyresetovani do puvodniho NORMAL stavu,
     * ktera by se mela zavolat po uspesnem nacteni dany stranky.
     */
    private makeProcessingState(pageNumber): () => void {
        this.state.page = states.PROCESSING;

        if (pageNumber === this.previousPageNumber) {
            this.state.previousPage = states.PROCESSING;
            return () => {
                this.state.page = states.NORMAL;
                this.state.previousPage = states.NORMAL;
            };
        } else if (pageNumber === this.nextPageNumber) {
            this.state.nextPage = states.PROCESSING;
            return () => {
                this.state.page = states.NORMAL;
                this.state.nextPage = states.NORMAL;
            };
        } else {
            this.state.distantPage = states.PROCESSING;
            return () => {
                this.state.page = states.NORMAL;
                this.state.distantPage = states.NORMAL;
            };
        }
    }

    /**
     * Vrati, zda je dana stranka navazujici na aktualne nactene
     *
     * @param pageNumber
     * @returns {boolean}
     */
    private isContiguous(pageNumber) {
        if (this.pages.length === 0) {
            return true;
        }
        return pageNumber === this.previousPageNumber || pageNumber === this.nextPageNumber;
    }

    private addPage(page: Page): void {
        // eslint-disable-next-line
        console.log('Pagination: got page ', page.pageNumber);

        removeByProperty(page.pageNumber, 'pageNumber')(this.pages);

        // kontrola na nacteni stranky, ktera nenavazuje na aktualne nactene
        if (!this.isContiguous(page.pageNumber)) { // pokud neni tato stranka primo navazujici (netvori souvislou radu se soucasnymi), ostatni smazeme
            this.clear();
        }

        this.pages.push(page);
        this.sortPages();

        this.previousPageNumber = this.pages[0].first ? -1 : this.pages[0].pageNumber - 1;
        this.nextPageNumber = this.pages[this.pages.length - 1].last ? -1 : this.pages[this.pages.length - 1].pageNumber + 1;
        this.recomputePageNumbers();

        this.items.length = 0;

        const allItems = this.pages.flatMap((currentPage) => currentPage.items);
        this.items = this.items.concat(allItems);
    }

    private sortPages() {
        this.pages.sort((a, b) => a.pageNumber - b.pageNumber);
    }

    private recomputePageNumbers() {
        this.showedPageNumbers.length = 0;

        if (this.totalPages <= 1) {
            return;
        }

        const currentPageNumber = this.pages[this.pages.length - 1].pageNumber;
        const numberOfShowedPages = 10;
        const firstShowedPage = currentPageNumber - (numberOfShowedPages / 2 - 2) < 1 ? 1 : currentPageNumber - (numberOfShowedPages / 2 - 2);
        const lastShowedPage = firstShowedPage + numberOfShowedPages - 1 > this.totalPages ? this.totalPages : firstShowedPage + numberOfShowedPages - 1;

        for (let i = firstShowedPage; i <= lastShowedPage; i++) {
            this.showedPageNumbers.push(i);
        }
    }

}
